import { useMemo, useState } from 'react';
import { IconButton } from '../../components/IconButton';
import { IconView } from '../../components/icons';
import Pagination from '../../components/Pagination';
import { labelFor, useConfig } from '../../context/ConfigContext';
import usePaginatedList from '../../hooks/usePaginatedList';

interface EmployeeRow {
  id: number;
  employee_code: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  current_position: {
    position: { position_title: string; status: string };
  } | null;
}

// A picker list for the Employee Pay page — that page is scoped to one
// employee, so it can't be reached directly from a static left-menu item.
// Mirrors Employees.tsx's table, but each row jumps straight to Pay
// instead of Edit.
export function Compensation() {
  const { lists } = useConfig();
  const [search, setSearch] = useState('');

  const filters = useMemo(() => ({ search: search || undefined }), [search]);
  const { data, loading, error, setPage } = usePaginatedList<EmployeeRow>('/employees', filters);

  return (
    <>
      <header className="page-header">
        <h1>Compensation</h1>
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Email</th>
                <th>Position</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((e) => (
                  <tr key={e.id}>
                    <td className="muted">{e.employee_code}</td>
                    <td>
                      <strong>
                        {e.first_name} {e.last_name}
                      </strong>
                    </td>
                    <td>{e.email}</td>
                    <td>{e.current_position ? e.current_position.position.position_title : '—'}</td>
                    <td>
                      {e.current_position ? (
                        <span className="badge badge-neutral">
                          {labelFor(lists, 'position_status', e.current_position.position.status)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="Manage pay" to={`/hr/employees/${e.id}/pay`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No employees found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
